import { useAtom } from "jotai";
import { useUpdateAtom } from "jotai/utils";
import React, { useEffect, useRef } from "react";
import {
  currentUserAtom,
  userLocationsAtom,
  updateFaceBlocksAtom,
  useCanvasRef,
  deleteFromFaceBlocksAtom,
  isPublishingVideoAtom,
  onlinesAtom,
} from "../jotai/atoms";
import { styled } from "../stitches.config";
import { AnyBlock, FaceBlock as FaceBlockType } from "../types";
import Icon from "../components/Icon";
import withSelectModifierKey from "../utils/withSelectModifierKey";
import { colorValue } from "../css/color";

// how far in pixels you have to scroll the facedock out of viewport to be "yoinked" back
const threshhold = 200;

function elementIsInViewport(elem: HTMLDivElement) {
  const bounding = elem.getBoundingClientRect();

  const innerHeight =
    window.innerHeight || document.documentElement.clientHeight;
  const innerWidth = window.innerWidth || document.documentElement.clientWidth;
  return (
    bounding.bottom >= -threshhold &&
    bounding.right >= -threshhold &&
    bounding.top <= innerHeight + threshhold &&
    bounding.left <= innerWidth + threshhold
  );
}

function FaceBlock({
  block,
  updateBlock,
}: {
  block: FaceBlockType;
  updateBlock: (block: Partial<AnyBlock>, raise?: boolean) => void;
}) {
  const userId = block.value;
  const [currentUser] = useAtom(currentUserAtom);
  const [onlines] = useAtom(onlinesAtom);
  const [userLocations] = useAtom(userLocationsAtom);
  const [isStreaming] = useAtom(isPublishingVideoAtom);
  const canvasRef = useCanvasRef();
  const updateFaceBlocks = useUpdateAtom(updateFaceBlocksAtom);
  const currentUserLocation = userLocations[currentUser!.id];
  const moved = useRef(false);
  const blockRef = useRef<HTMLDivElement>(null);
  const deleteFromFaceBlocks = useUpdateAtom(deleteFromFaceBlocksAtom);

  useEffect(() => {
    updateFaceBlocks(block);
    return () => {
      deleteFromFaceBlocks(block.id);
    };
  }, [block, deleteFromFaceBlocks, updateFaceBlocks]);

  function onMouseOver() {
    // If already in use, locked, or you're locked elsewhere
    const elsewhere =
      currentUserLocation &&
      currentUserLocation.block &&
      currentUserLocation.block !== block.id &&
      currentUserLocation.locked;

    if (
      (block.value && onlines.has(block.value)) ||
      block.lockedBy ||
      elsewhere ||
      !isStreaming
    ) {
      return;
    }

    updateBlock({ value: currentUser!.id }, false);
  }

  function onMouseLeave() {
    if (block.value !== currentUser!.id) return;
    if (block.lockedBy) return;

    updateBlock({ value: null }, false);
  }

  // sometimes mouseleave doesn't trigger because of framerate
  // brute force a bit with canvasRef
  useEffect(() => {
    if (!canvasRef) return;
    if (block.value !== currentUser!.id) return;
    if (block.lockedBy) return;

    function exitBlock(e: MouseEvent) {
      if (e.target !== e.currentTarget) return;
      updateBlock({ value: null }, false);
    }

    canvasRef.addEventListener("mousemove", exitBlock);
    return () => {
      canvasRef.removeEventListener("mousemove", exitBlock);
    };
  }, [canvasRef, updateBlock, block, currentUser]);

  function onClick(e: React.MouseEvent<HTMLElement>) {
    if (moved.current) return; // click event doesn't cancel if you are moving the block
    if (withSelectModifierKey(e)) return; // when user try to select multiple block
    if (block.value !== currentUser!.id) return; // docked by someone else
    if (block.lockedBy && block.lockedBy.id !== currentUser!.id) return; // locked by someone else

    // If unlocked, lock, otherwise, unlock (by setting null)
    updateBlock(
      {
        lockedBy: block.lockedBy ? null : currentUser,
      },
      // do not raise (put on top) when dock value changes
      false
    );
  }

  function onMouseMove() {
    moved.current = true;
  }

  function onMouseDown(e: React.MouseEvent) {
    moved.current = false;
    // Special case to make facedock lock work for positionally locked blocks
    if (block.locked) {
      e.stopPropagation();
    }
  }

  // Unlock if person leaves
  useEffect(() => {
    if (!block.lockedBy) {
      return;
    }

    // ignore when onlines are still loading, at least one value should be here (current user)
    if (onlines.size === 0) {
      return;
    }

    if (!onlines.has(block.lockedBy.id)) {
      updateBlock({
        value: null,
        lockedBy: null,
      });
    }
  }, [onlines, updateBlock, block]);

  // unlock if block scrolls out of viewport
  useEffect(() => {
    function onScroll() {
      if (blockRef.current && !elementIsInViewport(blockRef.current)) {
        updateBlock(
          {
            value: null,
            lockedBy: null,
          },
          false
        );
      }
    }

    if (blockRef && userId && currentUser && userId === currentUser?.id) {
      window.addEventListener("scroll", onScroll);
    }

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [blockRef, currentUser, updateBlock, userId]);

  return (
    <VideoContainer
      ref={blockRef}
      onMouseOver={onMouseOver}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {userId ? null : (
        <Empty>
          <HoverIcon />
        </Empty>
      )}
    </VideoContainer>
  );
}

export default FaceBlock;

const HoverIcon = styled(
  () => <Icon icon="FaceDock" color={colorValue("paper", 10, 0.2)} />,
  {
    transform: "scale(1.5)",
  }
);

const Empty = styled("div", {
  textAlign: "center",
  height: "100%",
  display: "grid",
  placeItems: "center",
  padding: "$2",
  fontSize: ".75rem",
});

const VideoContainer = styled("div", {
  position: "relative",
  userSelect: "none",
});
